import React, { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router";

import { ApplicationEndpoints } from "@generated/application";
import applicationApi from "src/domains/application/module/api/applicationApi";
import { ORGANIZATIONS, PROJECTS } from "src/platform/constants/routes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

import type { ExistingSystem, DetectedService } from "../StackEditPage/types";

const DEFAULT_RESOURCES = { cpu: "500m", memory: "512Mi" };

/** Avatar background color by service type */
const SERVICE_AVATAR_COLORS: Record<string, string> = {
  server: "primary.main",
  static: "secondary.main",
  worker: "#7c3aed",
  cron: "warning.main",
};

type ServiceStatus = "pending" | "approved" | "skipped" | "rejected";

const STATUS_LABELS: Record<ServiceStatus, string> = {
  pending: "Pending review",
  approved: "Approved — will be deployed",
  skipped: "Skipped — not deployed",
  rejected: "Rejected — excluded from stack",
};

const STATUS_COLORS: Record<ServiceStatus, string> = {
  pending: "warning.main",
  approved: "success.main",
  skipped: "text.secondary",
  rejected: "error.main",
};

function authHeader() {
  return { withCredentials: true as const };
}

/** Env var entry — either detected (required/optional) or manually added. */
interface EnvVarEntry {
  name: string;
  required: boolean;
  manual: boolean;
  /** Index into the manualEnvVars array (only for manual entries). */
  manualIndex?: number;
}

/** Build the env var list for a service from detected + manual entries. */
function buildEnvVarEntries(
  svc: DetectedService,
  manualList: { name: string; value: string }[],
): EnvVarEntry[] {
  const entries: EnvVarEntry[] = [];
  const seen = new Set<string>();

  for (const name of svc.requiredEnvVars ?? []) {
    entries.push({ name, required: true, manual: false });
    seen.add(name);
  }
  for (const name of svc.optionalEnvVars ?? []) {
    if (!seen.has(name)) {
      entries.push({ name, required: false, manual: false });
      seen.add(name);
    }
  }
  for (const env of svc.env ?? []) {
    if (!seen.has(env.name)) {
      entries.push({ name: env.name, required: false, manual: false });
      seen.add(env.name);
    }
  }
  // Manual entries get their own stable index
  manualList.forEach((m, idx) => {
    entries.push({
      name: m.name,
      required: false,
      manual: true,
      manualIndex: idx,
    });
  });
  return entries;
}

export default function ReviewServicesPage(): React.ReactElement {
  const { orgAlias, projectAlias, sysAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
    sysAlias: string;
  }>();
  const nav = useAppNavigation();

  const [system, setSystem] = useState<ExistingSystem | null>(null);
  const [services, setServices] = useState<DetectedService[]>([]);
  const [serviceStatuses, setServiceStatuses] = useState<
    Record<string, ServiceStatus>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [targetEnvironmentSlug, setTargetEnvironmentSlug] = useState("");
  const [targetEnvironments, setTargetEnvironments] = useState<
    { slug: string; name: string }[]
  >([]);
  // envValues: serviceName → envVarName → value
  const [envValues, setEnvValues] = useState<
    Record<string, Record<string, string>>
  >({});
  // manualEnvVars: serviceName → list of manually added { name, value }
  const [manualEnvVars, setManualEnvVars] = useState<
    Record<string, { name: string; value: string }[]>
  >({});

  useEffect(() => {
    if (!sysAlias) return;
    const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION.replace(":id", sysAlias)}`;
    axios
      .get<ExistingSystem>(url, { ...authHeader() })
      .then((r) => {
        setSystem(r.data);
        const stackConfig = (r.data.stackConfig ?? {}) as Record<
          string,
          unknown
        >;
        const detected = Array.isArray(stackConfig._detectedServices)
          ? (stackConfig._detectedServices as DetectedService[])
          : [];
        setServices(
          detected.map((s) => ({
            ...s,
            resources: s.resources ?? { ...DEFAULT_RESOURCES },
          })),
        );
        // Initialize all services as pending
        const initialStatuses: Record<string, ServiceStatus> = {};
        detected.forEach((s) => {
          initialStatuses[s.name] = "pending";
        });
        setServiceStatuses(initialStatuses);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err)
          ? ((err.response?.data as { message?: string })?.message ??
            err.message)
          : "Failed to load stack.";
        setError(msg);
        setLoading(false);
      });
  }, [sysAlias]);

  useEffect(() => {
    if (!projectAlias) return;
    axios
      .get<{ environments: { slug: string; name: string }[] }>(
        `${apiBaseUrls.BACKEND}/api/v1/environments`,
        { params: { projectAlias }, ...authHeader() },
      )
      .then((response) => {
        const deployable = response.data.environments.filter((environment) =>
          ["dev", "staging", "production"].includes(environment.slug),
        );
        setTargetEnvironments(deployable);
        setTargetEnvironmentSlug(
          (current) => current || deployable[0]?.slug || "",
        );
      })
      .catch(() => setTargetEnvironments([]));
  }, [projectAlias]);

  const setServiceStatus = useCallback(
    (svcName: string, status: ServiceStatus) => {
      setServiceStatuses((prev) => ({ ...prev, [svcName]: status }));
    },
    [],
  );

  const setEnvValue = useCallback(
    (svcName: string, varName: string, value: string) => {
      setEnvValues((prev) => ({
        ...prev,
        [svcName]: { ...(prev[svcName] ?? {}), [varName]: value },
      }));
    },
    [],
  );

  const addManualEnvVar = useCallback((svcName: string) => {
    setManualEnvVars((prev) => ({
      ...prev,
      [svcName]: [...(prev[svcName] ?? []), { name: "", value: "" }],
    }));
  }, []);

  const updateManualEnvVar = useCallback(
    (
      svcName: string,
      index: number,
      patch: Partial<{ name: string; value: string }>,
    ) => {
      setManualEnvVars((prev) => ({
        ...prev,
        [svcName]: (prev[svcName] ?? []).map((e, i) =>
          i === index ? { ...e, ...patch } : e,
        ),
      }));
    },
    [],
  );

  const removeManualEnvVar = useCallback((svcName: string, index: number) => {
    setManualEnvVars((prev) => ({
      ...prev,
      [svcName]: (prev[svcName] ?? []).filter((_, i) => i !== index),
    }));
  }, []);

  /** Collect all env var entries for a service (detected + manual). */
  const getAllEnvEntries = useCallback(
    (svc: DetectedService): EnvVarEntry[] => {
      return buildEnvVarEntries(svc, manualEnvVars[svc.name] ?? []);
    },
    [manualEnvVars],
  );

  /** Check if any required env var is unfilled. */
  const hasUnfilledRequired = useCallback(
    (svc: DetectedService): boolean => {
      const entries = getAllEnvEntries(svc);
      return entries.some(
        (e) => e.required && !(envValues[svc.name]?.[e.name] ?? "").trim(),
      );
    },
    [envValues, getAllEnvEntries],
  );

  const anyUnfilledRequired = services.some(hasUnfilledRequired);

  const handleSave = useCallback(async () => {
    if (!sysAlias || !orgAlias || !projectAlias) return;
    setSaving(true);
    setSaveError(null);
    try {
      // 1. Store env var values as secrets in OpenBao via the secrets API
      for (const svc of services) {
        const entries = getAllEnvEntries(svc);
        for (const entry of entries) {
          let value: string;
          if (entry.manual && entry.manualIndex !== undefined) {
            value = manualEnvVars[svc.name]?.[entry.manualIndex]?.value ?? "";
          } else {
            value = envValues[svc.name]?.[entry.name] ?? "";
          }
          if (!value.trim()) continue;

          const secretUrl = `${apiBaseUrls.BACKEND}/api/v1/applications/${sysAlias}/secrets/${encodeURIComponent(entry.name)}`;
          await axios.put(secretUrl, { value }, { ...authHeader() });
        }
      }

      // 2. Update stackConfig with env var declarations
      const servicesWithEnv = services.map((svc) => {
        const entries = getAllEnvEntries(svc);
        const env = entries
          .filter((e) => {
            let value: string;
            if (e.manual && e.manualIndex !== undefined) {
              value = manualEnvVars[svc.name]?.[e.manualIndex]?.value ?? "";
            } else {
              value = envValues[svc.name]?.[e.name] ?? "";
            }
            return value.trim() || e.required;
          })
          .map((e) => ({ name: e.name, secretKey: e.name }));
        return { ...svc, env: env.length > 0 ? env : undefined };
      });

      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", sysAlias)}`;
      await axios.patch(
        url,
        {
          stackConfig: {
            ...(system?.stackConfig ?? {}),
            _detectedServices: servicesWithEnv,
          },
        },
        { ...authHeader() },
      );

      // 3. Trigger build and deploy
      await applicationApi.buildAndDeploy(sysAlias, targetEnvironmentSlug);
      nav.goToStackDetails(orgAlias, projectAlias, sysAlias);
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? ((err.response?.data as { message?: string })?.message ?? err.message)
        : "Failed to save services.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [
    nav,
    orgAlias,
    projectAlias,
    services,
    sysAlias,
    system?.stackConfig,
    envValues,
    manualEnvVars,
    getAllEnvEntries,
    targetEnvironmentSlug,
  ]);

  // ─── Loading state ─────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
          <Skeleton
            variant="rounded"
            width="90%"
            height={20}
            sx={{ mb: 1.5 }}
          />
          <Skeleton
            variant="rounded"
            width="70%"
            height={20}
            sx={{ mb: 1.5 }}
          />
          <Skeleton variant="rounded" width="40%" height={20} />
        </Paper>
      </Container>
    );
  }

  // ─── Error state ───────────────────────────────────────────────
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setError(null);
                setLoading(true);
                // Re-trigger the effect by reloading
                window.location.reload();
              }}
            >
              Try again
            </Button>
          }
        >
          Failed to load detected services. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <RouterLink
            to={
              orgAlias
                ? ORGANIZATIONS.DETAILS.getPath(orgAlias)
                : ORGANIZATIONS.ROOT.path
            }
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {orgAlias}
          </RouterLink>
          {" / "}
          <RouterLink
            to={orgAlias ? PROJECTS.LIST.getPath(orgAlias) : "#"}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Projects
          </RouterLink>
          {" / "}
          <RouterLink
            to={
              orgAlias && projectAlias
                ? PROJECTS.DETAILS.getPath(orgAlias, projectAlias)
                : "#"
            }
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {projectAlias}
          </RouterLink>
          {" / "}
          <RouterLink
            to={
              orgAlias && projectAlias && sysAlias
                ? `/organizations/${orgAlias}/projects/${projectAlias}/stacks/${sysAlias}`
                : "#"
            }
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {system?.name ?? sysAlias}
          </RouterLink>
          {" / "}
          <Typography component="span" variant="body2" color="text.primary">
            Review Services
          </Typography>
        </Typography>
      </Box>

      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={700}>
              Review Detected Services
            </Typography>
            <Chip
              label={`${services.length} service${services.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                bgcolor: "rgba(59, 130, 246, 0.12)",
                color: "primary.main",
                fontWeight: 700,
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Approve, reject, or skip each detected service before adopting the
            stack.
          </Typography>
        </Box>
      </Stack>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {/* ─── Service list in single outer card ─────────────────────── */}
      {services.length === 0 ? (
        /* A7: Empty state replaces entire content when no services */
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Box
            component="svg"
            viewBox="0 0 180 160"
            sx={{ width: 180, height: 160, mb: 2 }}
            role="presentation"
            aria-hidden="true"
          >
            <rect
              x="40"
              y="40"
              width="100"
              height="80"
              rx="8"
              fill="rgba(59, 130, 246, 0.06)"
              stroke="rgba(59, 130, 246, 0.4)"
              strokeWidth="2"
            />
            <rect
              x="54"
              y="56"
              width="72"
              height="8"
              rx="3"
              fill="rgba(59, 130, 246, 0.25)"
            />
            <rect
              x="54"
              y="72"
              width="72"
              height="8"
              rx="3"
              fill="rgba(59, 130, 246, 0.25)"
            />
            <rect
              x="54"
              y="88"
              width="48"
              height="8"
              rx="3"
              fill="rgba(59, 130, 246, 0.25)"
            />
            <circle cx="140" cy="120" r="14" fill="rgba(59, 130, 246, 0.9)" />
            <text
              x="140"
              y="125"
              textAnchor="middle"
              fontSize="14"
              fontWeight="700"
              fill="#ffffff"
            >
              ?
            </text>
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            No services detected
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            No services were detected in the repository. Make sure your project
            has a rusha.yml manifest.
          </Typography>
          <Button
            variant="contained"
            onClick={() => nav.goToAdoptStack(orgAlias!, projectAlias!)}
          >
            Back to inspect →
          </Button>
        </Paper>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              {services.map((svc) => {
                const status = serviceStatuses[svc.name] ?? "pending";
                const entries = getAllEnvEntries(svc);
                const unfilled = hasUnfilledRequired(svc);
                const isSkipped = status === "skipped" || status === "rejected";

                return (
                  <Box
                    key={svc.name}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      opacity: isSkipped ? 0.7 : 1,
                    }}
                  >
                    {/* Service header row */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      spacing={1.75}
                    >
                      <Stack direction="row" spacing={1.75} alignItems="center">
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor:
                              SERVICE_AVATAR_COLORS[svc.type] ?? "primary.main",
                            fontSize: 18,
                            fontWeight: 700,
                            flexShrink: 0,
                            borderRadius: 1,
                          }}
                        >
                          {svc.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight={700}>
                            {svc.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Type:{" "}
                            {svc.type.charAt(0).toUpperCase() +
                              svc.type.slice(1)}
                            {svc.port ? ` · Port: ${svc.port}` : " · Port: —"}
                            {svc.dockerfilePath
                              ? ` · Dockerfile: ${svc.dockerfilePath}`
                              : ""}
                            {svc.healthEndpoint
                              ? ` · Health: ${svc.healthEndpoint}`
                              : ""}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Action buttons */}
                      {!isSkipped && (
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant={
                              status === "approved" ? "contained" : "outlined"
                            }
                            color="primary"
                            onClick={() =>
                              setServiceStatus(svc.name, "approved")
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setServiceStatus(svc.name, "skipped")
                            }
                          >
                            Skip
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() =>
                              setServiceStatus(svc.name, "rejected")
                            }
                          >
                            Reject
                          </Button>
                        </Stack>
                      )}
                      {isSkipped && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setServiceStatus(svc.name, "pending")}
                        >
                          Re-include
                        </Button>
                      )}
                    </Stack>

                    {/* Status label */}
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: STATUS_COLORS[status],
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {status === "pending" && "⏳ "}
                      {status === "approved" && "✓ "}
                      {status === "skipped" && "⊘ "}
                      {status === "rejected" && "✕ "}
                      {STATUS_LABELS[status]}
                    </Typography>

                    {/* ─── Environment Variables section ───────────────── */}
                    {!isSkipped && (
                      <>
                        <Divider sx={{ mt: 2, mb: 2 }} />
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            Environment Variables
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                            onClick={() => addManualEnvVar(svc.name)}
                            sx={{ textTransform: "none" }}
                          >
                            Add variable
                          </Button>
                        </Stack>

                        {entries.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 1 }}
                          >
                            No environment variables detected for this service.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {entries.map((entry, ei) => {
                              const value =
                                entry.manual && entry.manualIndex !== undefined
                                  ? (manualEnvVars[svc.name]?.[
                                      entry.manualIndex
                                    ]?.value ?? "")
                                  : (envValues[svc.name]?.[entry.name] ?? "");
                              const isEmpty = !value.trim();

                              return (
                                <Stack
                                  key={`env-${ei}`}
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {/* Name field */}
                                  {entry.manual ? (
                                    <TextField
                                      size="small"
                                      placeholder="VARIABLE_NAME"
                                      value={entry.name}
                                      onChange={(e) =>
                                        updateManualEnvVar(
                                          svc.name,
                                          entry.manualIndex!,
                                          { name: e.target.value },
                                        )
                                      }
                                      sx={{
                                        width: 240,
                                        fontFamily: "monospace",
                                        "& .MuiOutlinedInput-root": {
                                          borderStyle: "dashed",
                                          borderWidth: 2,
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        flex: "1 1 220px",
                                        minWidth: 220,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        fontFamily: "monospace",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {entry.name}
                                      {entry.required && (
                                        <Chip
                                          label="Required"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            bgcolor: "rgba(239, 68, 68, 0.12)",
                                            color: "error.main",
                                          }}
                                        />
                                      )}
                                      {!entry.required && (
                                        <Chip
                                          label="Optional"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            bgcolor:
                                              "rgba(156, 163, 175, 0.12)",
                                            color: "text.secondary",
                                          }}
                                        />
                                      )}
                                    </Box>
                                  )}

                                  {/* Value field */}
                                  <TextField
                                    size="small"
                                    type="password"
                                    placeholder={
                                      entry.required
                                        ? "Enter value…"
                                        : "Optional…"
                                    }
                                    value={value}
                                    onChange={(e) => {
                                      if (
                                        entry.manual &&
                                        entry.manualIndex !== undefined
                                      ) {
                                        updateManualEnvVar(
                                          svc.name,
                                          entry.manualIndex,
                                          { value: e.target.value },
                                        );
                                      } else {
                                        setEnvValue(
                                          svc.name,
                                          entry.name,
                                          e.target.value,
                                        );
                                      }
                                    }}
                                    sx={{
                                      flex: 1,
                                      fontFamily: "monospace",
                                    }}
                                    error={entry.required && isEmpty}
                                    InputProps={{
                                      startAdornment:
                                        entry.required && isEmpty ? (
                                          <InputAdornment position="start">
                                            <WarningAmberIcon
                                              color="error"
                                              sx={{ fontSize: 16 }}
                                            />
                                          </InputAdornment>
                                        ) : undefined,
                                    }}
                                  />

                                  {/* Remove button (manual only) */}
                                  {entry.manual && (
                                    <IconButton
                                      size="small"
                                      aria-label="Delete variable"
                                      onClick={() =>
                                        removeManualEnvVar(
                                          svc.name,
                                          entry.manualIndex!,
                                        )
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Stack>
                              );
                            })}
                          </Stack>
                        )}

                        {unfilled && (
                          <Alert
                            severity="warning"
                            sx={{
                              mt: 1.5,
                              py: 0.5,
                              "& .MuiAlert-message": { fontSize: 13 },
                            }}
                          >
                            Required variable(s) are empty. Fill them in before
                            deploying.
                          </Alert>
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Paper>

          <Alert severity="info" variant="outlined" sx={{ mt: 3, mb: 3 }}>
            Need a database, cache, or queue? Use the <strong>Resources</strong>{" "}
            tab on the stack page to provision them independently and attach
            them to this stack.
          </Alert>

          {/* A6: Page actions right-aligned */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            alignItems="center"
          >
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="review-deploy-target-label">
                Target environment
              </InputLabel>
              <Select
                labelId="review-deploy-target-label"
                value={targetEnvironmentSlug}
                label="Target environment"
                onChange={(event) =>
                  setTargetEnvironmentSlug(event.target.value)
                }
                disabled={saving || targetEnvironments.length === 0}
              >
                {targetEnvironments.map((environment) => (
                  <MenuItem key={environment.slug} value={environment.slug}>
                    {environment.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="text"
              onClick={() => nav.goToAdoptStack(orgAlias!, projectAlias!)}
            >
              Back to inspect
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? null : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || anyUnfilledRequired || !targetEnvironmentSlug}
            >
              {saving ? "Saving…" : "Confirm & Deploy"}
            </Button>
          </Stack>
        </>
      )}
    </Container>
  );
}
